<template>
  <div class="policies-page">
    <!-- Header -->
    <div class="page-header">
      <h1 class="page-title">School Policies</h1>
      <p class="page-subtitle">Browse our comprehensive policies and guidelines</p>
    </div>

    <!-- Policy Cards Grid -->
    <div class="policies-container">
      <div class="policies-grid">
        <div
          v-for="(policy, index) in policies"
          :key="index"
          class="policy-card"
          :style="{ animationDelay: `${index * 0.1}s` }"
        >
          <div class="card-icon" :style="{ background: policy.color }">
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
              <path d="M14 6h20l8 8v28a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2z" stroke="white" stroke-width="2" fill="none"/>
              <path d="M34 6v8h8M16 24h16M16 30h16M16 36h10" stroke="white" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </div>

          <div class="card-content">
            <h3 class="card-title">{{ policy.title }}</h3>
            <p class="card-description">{{ policy.description }}</p>

            <div class="card-meta">
              <span class="meta-item">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M14 2H2v12h12V2z" stroke="currentColor" stroke-width="1.5" fill="none"/>
                  <path d="M4 6h8M4 9h8M4 12h5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                </svg>
                {{ policy.pages }} pages
              </span>
              <span class="meta-item">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <circle cx="8" cy="8" r="6" stroke="currentColor" stroke-width="1.5" fill="none"/>
                  <path d="M8 4v4l3 2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                </svg>
                {{ policy.lastUpdated }}
              </span>
            </div>
          </div>

          <button class="view-policy-btn" @click="openPolicyViewer(policy)">
            <span>View Policy</span>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M7 4l6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- PDF Viewer Popup -->
    <transition name="viewer-popup">
      <div v-if="showPolicyViewer" class="viewer-popup" @click.self="closePolicyViewer">
        <div class="viewer-container">

          <!-- Header -->
          <div class="viewer-header">
            <div class="viewer-title">
              <div class="title-icon" :style="{ background: selectedPolicy?.color }">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M7 3h10l4 4v14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z" stroke="white" stroke-width="2" fill="none"/>
                </svg>
              </div>
              <div>
                <h2>{{ selectedPolicy?.title }}</h2>
                <p>{{ totalPages }} pages • Last updated {{ selectedPolicy?.lastUpdated }}</p>
              </div>
            </div>

            <div class="viewer-actions">
              <!-- Open in new tab -->
              <button class="action-btn" @click="zoomIn" title="Open in new tab">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M11 3h6v6M17 3l-8 8M8 5H4a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1v-4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>

              <!-- Download -->
              <button class="action-btn download-btn" @click="downloadPdf" title="Download PDF" :disabled="!selectedPolicy?.pdfPath">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M10 3v10m0 0l-4-4m4 4l4-4M3 17h14" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>

              <!-- Close -->
              <button class="action-btn close-viewer" @click="closePolicyViewer" title="Close">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M15 5L5 15M5 5l10 10" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                </svg>
              </button>
            </div>
          </div>

          <!-- PDF / Image Viewer -->
          <div class="pdf-content">
            <!-- Loading -->
            <div v-if="isLoadingPdf" class="pdf-state">
              <div class="loading-spinner"></div>
              <p>Loading...</p>
            </div>

            <!-- Error -->
            <div v-else-if="pdfError" class="pdf-state error-state">
              <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
                <circle cx="32" cy="32" r="30" stroke="#ff4757" stroke-width="2" fill="none"/>
                <path d="M32 20v16M32 44v2" stroke="#ff4757" stroke-width="2" stroke-linecap="round"/>
              </svg>
              <p>{{ pdfError }}</p>
              <a v-if="selectedPolicy?.pdfPath" :href="selectedPolicy.pdfPath" target="_blank" class="retry-btn">Open in new tab</a>
            </div>

            <!-- PDF iframe -->
            <iframe
              v-else-if="viewMode === 'pdf' && embedSrc"
              :src="embedSrc"
              class="pdf-iframe"
              frameborder="0"
              @load="isLoadingPdf = false"
              @error="onIframeError"
            ></iframe>

            <!-- Image fallback -->
            <div v-else-if="viewMode === 'images' && imagePages.length" class="image-viewer">
              <img
                :src="imagePages[currentPage - 1]"
                :alt="`${selectedPolicy?.title} - Page ${currentPage}`"
                class="policy-image"
              />
            </div>
          </div>

          <!-- Navigation (only for image mode) -->
          <div v-if="viewMode === 'images'" class="viewer-navigation">
            <button class="nav-btn" @click="previousPage" :disabled="currentPage <= 1">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M12 4l-6 6 6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              Previous
            </button>
            <div class="page-info">
              <strong>{{ currentPage }}</strong> / {{ totalPages }}
            </div>
            <button class="nav-btn" @click="nextPage" :disabled="currentPage >= totalPages">
              Next
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M8 4l6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>

        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import api from '@/services/api';
import { mediaUrl } from '@/services/media';



export default {
  name: 'PoliciesPage',
  data() {
    return {
      showPolicyViewer: false,
      selectedPolicy: null,
      isLoadingPdf: false,
      pdfError: null,
      embedSrc: '',
      totalPages: 0,
      viewMode: 'pdf',      // 'pdf' | 'images'
      imagePages: [],
      currentPage: 1,

      policies: [
        {
          id: 1,
          title: 'Back to School Guidelines',
          description: 'Guidelines for honest academic work.',
          pages: 3,
          lastUpdated: 'Jan 2026',
          color: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          pdfPath: ''
        },
      ]
    };
  },

  async created() {
    try {
      const data = await api.getPolicies();
      const list = api.extractList(data);
      if (list.length > 0) {
        const defaultColors = [
          'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
          'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
          'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
          'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
          'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
          'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
        ];
        this.policies = list.map((p, i) => ({
          id: p.id,
          title: p.title,
          description: p.description || '',
          pages: p.page_count || 1,
          lastUpdated: p.last_updated || (p.updated_at ? new Date(p.updated_at).toLocaleDateString('en', { month: 'short', year: 'numeric' }) : 'N/A'),
          color: p.color_css || defaultColors[i % defaultColors.length],
          pdfPath: p.pdf_file ? mediaUrl(p.pdf_file) : '',
        }));
      }
    } catch (e) {
      console.warn('[Policies] API failed, using fallback:', e.message);
    }
  },

  beforeUnmount() {
    document.body.style.overflow = 'auto';
  },

  methods: {
    async openPolicyViewer(policy) {
      this.selectedPolicy = policy;
      this.pdfError = null;
      this.isLoadingPdf = true;
      this.embedSrc = '';
      this.viewMode = 'pdf';        // 'pdf' | 'images'
      this.currentPage = 1;
      this.totalPages = policy.pages || 0;
      this.imagePages = [];
      this.showPolicyViewer = true;
      document.body.style.overflow = 'hidden';

      // 1) Try to load API pages (images) in background for fallback
      if (policy.id && (!policy.apiPages || policy.apiPages.length === 0)) {
        try {
          const detail = await api.getPolicy(policy.id);
          if (detail?.pages?.length) {
            policy.apiPages = detail.pages.map(pg => mediaUrl(pg.image));
          }
        } catch (e) { /* no images available */ }
      }

      // 2) If PDF exists — show it
      if (policy.pdfPath) {
        this.viewMode = 'pdf';
        this.isLoadingPdf = false;
        await this.$nextTick();
        this.embedSrc = policy.pdfPath;
        return;
      }

      // 3) No PDF — fall back to images
      if (policy.apiPages?.length) {
        this.viewMode = 'images';
        this.imagePages = policy.apiPages;
        this.totalPages = this.imagePages.length;
        this.isLoadingPdf = false;
        return;
      }

      // 4) Nothing available
      this.isLoadingPdf = false;
      this.pdfError = 'No PDF or images available for this policy.';
    },

    closePolicyViewer() {
      this.showPolicyViewer = false;
      this.selectedPolicy = null;
      this.embedSrc = '';
      this.pdfError = null;
      this.imagePages = [];
      document.body.style.overflow = 'auto';
    },

    onIframeError() {
      // PDF iframe failed — try images fallback
      if (this.selectedPolicy?.apiPages?.length) {
        this.viewMode = 'images';
        this.imagePages = this.selectedPolicy.apiPages;
        this.totalPages = this.imagePages.length;
        this.currentPage = 1;
        this.embedSrc = '';
      } else {
        this.pdfError = 'Could not display the PDF. Try opening it directly.';
      }
    },

    nextPage() {
      if (this.currentPage < this.totalPages) this.currentPage++;
    },
    previousPage() {
      if (this.currentPage > 1) this.currentPage--;
    },

    zoomIn()  { if (this.selectedPolicy?.pdfPath) window.open(this.selectedPolicy.pdfPath, '_blank'); },

    downloadPdf() {
      if (!this.selectedPolicy?.pdfPath) return;
      const link = document.createElement('a');
      link.href = this.selectedPolicy.pdfPath;
      link.download = this.selectedPolicy.title.replace(/[^a-z0-9]/gi, '_').toLowerCase() + '.pdf';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
  }
};
</script>

<style scoped>
/* ── Page Layout ──────────────────────────────────────────────────────────── */
.policies-page {
  min-height: 100vh;
  background: white;
  padding: 60px 20px;
}

.page-header {
  text-align: center;
  margin-bottom: 60px;
  animation: fadeInDown 0.8s ease;
}

.page-title {
  font-size: 48px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0 0 15px 0;
}

.page-subtitle {
  font-size: 18px;
  color: #2c3e50;
  margin: 0;
}

/* ── Grid ─────────────────────────────────────────────────────────────────── */
.policies-container { max-width: 1400px; margin: 0 auto; }

.policies-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 30px;
}

/* ── Card ─────────────────────────────────────────────────────────────────── */
.policy-card {
  background: white;
  border-radius: 20px;
  padding: 30px;
  box-shadow: 0 10px 40px rgba(0,0,0,.1);
  transition: all 0.4s cubic-bezier(0.34,1.56,0.64,1);
  animation: scaleIn 0.6s ease backwards;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
}

.policy-card::before {
  content: '';
  position: absolute;
  top: 0; left: 0; right: 0;
  height: 4px;
  background: linear-gradient(135deg, #c02131c9 0%, #f7f02bb9 100%);
}

.policy-card:hover {
  transform: translateY(-10px);
  box-shadow: 0 20px 60px rgba(0,0,0,.2);
}

.card-icon {
  width: 80px; height: 80px;
  border-radius: 16px;
  display: flex; align-items: center; justify-content: center;
  margin-bottom: 20px;
  transition: transform 0.3s ease;
}
.policy-card:hover .card-icon { transform: scale(1.1) rotate(5deg); }

.card-content { flex: 1; margin-bottom: 20px; }

.card-title {
  font-size: 22px; font-weight: 700; color: #2c3e50;
  margin: 0 0 12px;
}

.card-description { font-size: 15px; color: #666; line-height: 1.6; margin: 0 0 20px; }

.card-meta { display: flex; gap: 20px; flex-wrap: wrap; }
.meta-item { display: flex; align-items: center; gap: 6px; font-size: 14px; color: #888; }

.view-policy-btn {
  width: 100%; padding: 14px 24px; border: none; border-radius: 12px;
  background: linear-gradient(135deg, #c02131c9 0%, #f7f02bb9 100%);
  color: white; font-size: 16px; font-weight: 600; cursor: pointer;
  transition: all 0.3s ease;
  display: flex; align-items: center; justify-content: center; gap: 10px;
}
.view-policy-btn:hover { transform: scale(1.02); box-shadow: 0 8px 20px rgba(102,126,234,.4); }
.view-policy-btn:hover svg { transform: translateX(5px); }
.view-policy-btn svg { transition: transform 0.3s ease; }

/* ── Popup Overlay ────────────────────────────────────────────────────────── */
.viewer-popup {
  position: fixed; inset: 0;
  background: rgba(0,0,0,.95);
  backdrop-filter: blur(10px);
  z-index: 3000;
  display: flex; align-items: center; justify-content: center;
  padding: 20px;
}

.viewer-container {
  width: 100%; max-width: 1000px; height: 92vh;
  background: white; border-radius: 20px; overflow: hidden;
  display: flex; flex-direction: column;
  box-shadow: 0 20px 60px rgba(0,0,0,.5);
  animation: slideUp 0.4s ease;
}

/* ── Header ───────────────────────────────────────────────────────────────── */
.viewer-header {
  display: flex; justify-content: space-between; align-items: center;
  padding: 18px 24px;
  background: #f8f9fa; border-bottom: 1px solid #e0e0e0;
  gap: 16px; flex-wrap: wrap;
}

.viewer-title { display: flex; align-items: center; gap: 14px; flex: 1; min-width: 220px; }

.title-icon {
  width: 46px; height: 46px; border-radius: 12px;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}

.viewer-title h2 { font-size: 18px; font-weight: 700; color: #2c3e50; margin: 0 0 4px; }
.viewer-title p  { font-size: 12px; color: #888; margin: 0; }

.viewer-actions { display: flex; align-items: center; gap: 8px; }

.action-btn {
  width: 38px; height: 38px;
  border: 1px solid #e0e0e0; border-radius: 8px;
  background: white; color: #666; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: all 0.2s ease;
}
.action-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.action-btn:not(:disabled):hover { background: #f0f0f0; border-color: #ccc; }
.action-btn.download-btn:not(:disabled):hover { background: #4CAF50; border-color: #4CAF50; color: white; }
.action-btn.close-viewer { background: #ff4757; border-color: #ff4757; color: white; }
.action-btn.close-viewer:hover { background: #ff3838; }

.zoom-level { font-size: 13px; font-weight: 600; color: #555; min-width: 46px; text-align: center; }

/* ── PDF iframe ───────────────────────────────────────────────────────────── */
.pdf-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
  background: #e8e8e8;
}

.pdf-iframe {
  flex: 1;
  width: 100%;
  height: 100%;
  border: none;
  display: block;
}

/* ── States ───────────────────────────────────────────────────────────────── */
.pdf-state {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 16px; color: #555; padding: 60px 40px; text-align: center;
}

.error-state { color: #ff4757; }
.error-detail { font-size: 13px; color: #888; font-family: monospace; word-break: break-all; }

.retry-btn {
  margin-top: 8px; padding: 10px 24px; border: none; border-radius: 8px;
  background: #667eea; color: white; font-size: 14px; font-weight: 600;
  cursor: pointer; transition: background 0.2s;
}
.retry-btn:hover { background: #5a6fd6; }

.loading-spinner {
  width: 48px; height: 48px;
  border: 4px solid rgba(0,0,0,.15);
  border-top-color: #667eea;
  border-radius: 50%;
  animation: spin 0.9s linear infinite;
}

/* ── Image fallback viewer ────────────────────────────────────────────────── */
.image-viewer {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  overflow: auto;
  background: #e8e8e8;
}

.policy-image {
  max-width: 100%;
  max-height: calc(90vh - 220px);
  object-fit: contain;
  border-radius: 6px;
  box-shadow: 0 4px 24px rgba(0,0,0,.25);
  background: white;
}

/* ── Navigation (image mode) ──────────────────────────────────────────────── */
.viewer-navigation {
  display: flex; justify-content: space-between; align-items: center;
  padding: 14px 24px;
  background: #f8f9fa; border-top: 1px solid #e0e0e0;
}

.nav-btn {
  padding: 9px 18px; border: 1px solid #e0e0e0; border-radius: 8px;
  background: white; color: #555; font-size: 14px; font-weight: 500;
  cursor: pointer; display: flex; align-items: center; gap: 6px;
  transition: all 0.2s ease;
}
.nav-btn:hover:not(:disabled) { background: #667eea; color: white; border-color: #667eea; }
.nav-btn:disabled { opacity: 0.4; cursor: not-allowed; }

.page-info { display: flex; align-items: center; gap: 8px; font-size: 14px; color: #666; }

.page-input {
  width: 58px; padding: 7px 10px; border: 1px solid #e0e0e0; border-radius: 6px;
  text-align: center; font-size: 14px; font-weight: 600;
}
.page-input:focus { outline: none; border-color: #667eea; }

/* ── Animations ───────────────────────────────────────────────────────────── */
@keyframes fadeInDown {
  from { opacity: 0; transform: translateY(-30px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes scaleIn {
  from { opacity: 0; transform: scale(0.9); }
  to   { opacity: 1; transform: scale(1); }
}
@keyframes slideUp {
  from { opacity: 0; transform: translateY(50px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes spin { to { transform: rotate(360deg); } }

.viewer-popup-enter-active,
.viewer-popup-leave-active { transition: all 0.35s ease; }
.viewer-popup-enter-from,
.viewer-popup-leave-to { opacity: 0; }
.viewer-popup-enter-from .viewer-container,
.viewer-popup-leave-to .viewer-container { transform: scale(0.92) translateY(40px); }

/* ── Responsive ───────────────────────────────────────────────────────────── */
@media (max-width: 768px) {
  .policies-grid { grid-template-columns: 1fr; }
  .page-title { font-size: 34px; }
  .viewer-container { height: 96vh; border-radius: 0; }
  .viewer-header { padding: 12px 16px; }
  .viewer-title { flex-direction: column; align-items: flex-start; }
  .viewer-actions { width: 100%; justify-content: flex-end; }
  .viewer-navigation { padding: 10px 16px; }
  .pdf-content { padding: 12px; }
}
</style>